import * as React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useState, useRef, useCallback, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import AppToast from '../../components/Toast/AppToast';
import {
  Button,
  TextField,
  FormControl,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Header from "../Includes/Header";
import DateFilter from "../FollowUps/DateFilter.jsx";
import { useTheme } from "../../contexts/ThemeContext";

const LIMIT = 10;
const getStyles = (colors, isDarkMode) => ({
  mainBox: { display: "flex", minHeight: "100vh", backgroundColor: isDarkMode ? "#121212" : "#EFEDF1" },
  contentBox: { flexGrow: 1, overflowY: "auto", padding: { xs: "8px", sm: "16px" }, marginTop: "64px" },
  pageTitle: { fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 700, color: colors.leaf, marginBottom: "4px" },
  addButton: {
    backgroundColor: colors.leaf,
    color: "#ffffff",
    "&:hover": { backgroundColor: colors.pine },
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    padding: "8px 16px",
    boxShadow: "0 4px 6px -1px rgba(31, 122, 84, 0.1)",
  },
  tableContainer: { backgroundColor: colors.card, borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(31, 122, 84, 0.1)", overflow: "hidden" },
  tableHead: {
    backgroundColor: colors.evergreen,
    "& th": { color: "#ffffff", fontWeight: 600, fontSize: { xs: "13px", sm: "14px", md: "15px" }, padding: { xs: "12px 8px", sm: "16px" }, whiteSpace: "nowrap" },
  },
  tableCell: { padding: { xs: "8px", sm: "16px" }, fontSize: { xs: "12px", sm: "14px" }, color: colors.ink, whiteSpace: "nowrap" },
  tableRow: {
    "&:nth-of-type(odd)": { backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "#f8fafc" },
    "&:hover": { backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(31, 122, 84, 0.04)", transition: "background-color 0.2s" },
  },
  statusButton: (status) => ({
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "20px",
    padding: "6px 16px",
    fontSize: { xs: "12px", sm: "13px" },
    backgroundColor: status === 1 ? "#22c55e" : "#ef4444",
    color: "#ffffff",
    "&:hover": { backgroundColor: status === 1 ? "#16a34a" : "#dc2626" },
  }),
  actionButton: { padding: "8px", "& .MuiSvgIcon-root": { fontSize: { xs: "18px", sm: "20px" } } },
  loadMoreButton: {
    backgroundColor: colors.leaf,
    color: "#ffffff",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    padding: "8px 24px",
    marginTop: "24px",
    "&:hover": { backgroundColor: colors.pine },
    "&.Mui-disabled": { backgroundColor: "#94a3b8", color: "#ffffff" },
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.card,
      borderRadius: "8px",
      "& fieldset": { borderColor: "rgba(31, 122, 84, 0.2)" },
      "&:hover fieldset": { borderColor: "rgba(31, 122, 84, 0.3)" },
      "&.Mui-focused fieldset": { borderColor: colors.leaf },
    },
  },
  dialogPaper: { borderRadius: "12px", backgroundColor: colors.card },
  dialogTitle: { color: colors.leaf, fontSize: { xs: "1.1rem", sm: "1.25rem" }, fontWeight: 600, borderBottom: `1px solid rgba(31, 122, 84, 0.1)`, padding: "16px 24px" },
  dialogActions: { padding: "16px 24px", borderTop: `1px solid rgba(31, 122, 84, 0.1)` },
});

function ExpenseListPage() {
  const { colors, isDarkMode } = useTheme();
  const styles = React.useMemo(() => getStyles(colors, isDarkMode), [colors, isDarkMode]);
  const dateRef = useRef();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [dateFilterValue, setDateFilterValue] = useState("");
  const [index, setIndex] = useState(0);
  const [expenseRows, setExpenseRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [fieldDescription, setFieldDescription] = useState("");
  const [fieldCategory, setFieldCategory] = useState("");
  const [fieldAmount, setFieldAmount] = useState("");
  const [fieldDate, setFieldDate] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [options, setOptions] = useState([]);
  const [openToast, setOpenToast] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const getToken = () => JSON.parse(localStorage.getItem("userdata") || "{}")?.token;

  const fetchExpenses = useCallback(
    async (reset = true, indexOverride = null) => {
      const token = getToken();
      if (!token) return;
      const nextIndex = reset ? 0 : indexOverride != null ? indexOverride : index;
      if (reset) {
        setLoading(true);
        setExpenseRows([]);
      } else setLoadingMore(true);
      try {
        const body = {
          limit: LIMIT,
          index: nextIndex,
          ...(searchText && { search_text: searchText }),
          ...(dateFilterValue && { date: { type: 1, value: dateFilterValue } }),
        };
        const res = await fetch(`${window.constants.api_url}/expense/retrive`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : [];
        const totalCount = data.total != null ? data.total : list.length;
        if (reset) {
          setExpenseRows(list);
          setIndex(LIMIT);
        } else {
          setExpenseRows((prev) => [...prev, ...list]);
          setIndex((i) => i + LIMIT);
        }
        setTotal(totalCount);
        setHasMore(list.length >= LIMIT && (totalCount == null || nextIndex + list.length < totalCount));
      } catch (e) {
        console.error("Error fetching expenses:", e);
        if (reset) setExpenseRows([]);
        setHasMore(false);
        setSnackbarMessage(e?.message || "Failed to load expenses");
        setSnackbarSeverity("error");
        setOpenToast(true);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [searchText, dateFilterValue, index]
  );

  const fetchAutocomplete = useCallback(async (value) => {
    const token = getToken();
    if (!token || !value || value.length < 2) {
      setOptions([]);
      return;
    }
    try {
      const res = await fetch(`${window.constants.api_url}/expense/autocomplete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ search_text: value }),
      });
      const data = await res.json();
      const list = Array.isArray(data.data) ? data.data : [];
      setOptions(list.map((item) => (typeof item === "string" ? item : item?.search_text ?? item?.search_data ?? item?.text ?? String(item))));
    } catch (e) {
      setOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchExpenses(true);
  }, [searchText, dateFilterValue]);

  const handleSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenToast(true);
  };

  const fetchData = useCallback(() => {
    setDateFilterValue("");
    setSearchText("");
    setSearchInput("");
    setIndex(0);
    fetchExpenses(true);
  }, [fetchExpenses]);

  const handleAddOpen = () => {
    setModalMode("add");
    setEditId(null);
    setFieldDescription("");
    setFieldCategory("");
    setFieldAmount("");
    setFieldDate(new Date().toISOString().slice(0, 10));
    setFormSubmitted(false);
    setAddModalOpen(true);
  };

  const handleAddClose = () => {
    setAddModalOpen(false);
    setFormSubmitted(false);
  };

  const handleApplyDate = (value) => {
    setDateFilterValue(value || "");
  };

  const handleApplyDateCustom = () => {
    fetchExpenses(true);
  };

  const handleSearch = () => {
    setSearchText(searchInput.trim());
  };

  const handleInputChange = (value) => {
    setSearchInput(value || "");
    fetchAutocomplete(value || "");
  };

  const handleLoadMore = () => {
    fetchExpenses(false, expenseRows.length);
  };

  const handleDelete = (id) => {
    setDeleteConfirmId(id);
    setOpenDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId == null) return;
    const token = getToken();
    if (!token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${window.constants.api_url}/expense/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: deleteConfirmId }),
      });
      const data = await res.json();
      if (data.code === 200 || data.code === "200") {
        handleSnackbar(data.message || "Expense deleted", "success");
        setExpenseRows((prev) => prev.filter((r) => r.id !== deleteConfirmId));
        setDeleteConfirmId(null);
        setOpenDeleteConfirmation(false);
      } else {
        handleSnackbar(data?.message || "Delete failed", "error");
      }
    } catch (e) {
      console.error("Delete expense error:", e);
      handleSnackbar(e?.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (row) => {
    if (row.status === 1) return;
    const token = getToken();
    if (!token) return;
    setStatusChangingId(row.id);
    try {
      const res = await fetch(`${window.constants.api_url}/expense/change-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: row.id }),
      });
      const data = await res.json();
      if (data.code === 200 || data.code === "200") {
        handleSnackbar(data.message || "Status updated", "success");
        setExpenseRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: 1 } : r)));
      } else {
        handleSnackbar(data?.message || "Status update failed", "error");
      }
    } catch (e) {
      console.error("Change status error:", e);
      handleSnackbar(e?.message || "Status update failed", "error");
    } finally {
      setStatusChangingId(null);
    }
  };

  const handleEditOpen = (row) => {
    setModalMode("edit");
    setEditId(row.id);
    setFieldDescription(row.description || "");
    setFieldCategory(row.category || "");
    setFieldAmount(row.amount != null ? String(row.amount) : "");
    setFieldDate(row.date ? String(row.date).slice(0, 10) : "");
    setFormSubmitted(false);
    setAddModalOpen(true);
  };

  const handleSaveExpense = async () => {
    setFormSubmitted(true);
    if (!fieldDescription.trim() || !fieldAmount.trim()) return;
    const token = getToken();
    if (!token) return;
    setSaving(true);
    try {
      if (modalMode === "add") {
        const res = await fetch(`${window.constants.api_url}/expense/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            description: fieldDescription.trim(),
            category: fieldCategory.trim() || "General",
            amount: Number(fieldAmount) || 0,
            date: fieldDate || new Date().toISOString().slice(0, 10),
          }),
        });
        const data = await res.json();
        if (data.code === 200 || data.code === "200") {
          handleSnackbar(data.message || "Expense added", "success");
          handleAddClose();
          fetchExpenses(true);
        } else {
          handleSnackbar(data?.message || "Add failed", "error");
        }
      } else if (editId != null) {
        const res = await fetch(`${window.constants.api_url}/expense/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            id: editId,
            description: fieldDescription.trim(),
            category: fieldCategory.trim() || "General",
            amount: Number(fieldAmount) || 0,
            date: fieldDate || new Date().toISOString().slice(0, 10),
          }),
        });
        const data = await res.json();
        if (data.code === 200 || data.code === "200") {
          handleSnackbar(data.message || "Expense updated", "success");
          handleAddClose();
          fetchExpenses(true);
        } else {
          handleSnackbar(data?.message || "Update failed", "error");
        }
      }
    } catch (e) {
      console.error("Save expense error:", e);
      handleSnackbar(e?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const s = String(d);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [y, m, day] = s.slice(0, 10).split("-");
      return `${day}-${m}-${y}`;
    }
    return s;
  };

  return (
    <Box sx={styles.mainBox}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={styles.contentBox}>
        <Container maxWidth="xl" sx={{ px: { xs: "8px", sm: "16px" } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography sx={styles.pageTitle}>All Expenses</Typography>
            <Button variant="contained" startIcon={<ControlPointIcon />} onClick={handleAddOpen} sx={styles.addButton}>
              Add New
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 3 }}>
            <Box sx={{ flex: { xs: "1", sm: "0 0 300px" } }}>
              <Autocomplete
                freeSolo
                disableClearable
                options={options}
                inputValue={searchInput}
                onInputChange={(_, value) => handleInputChange(value)}
                onChange={(_, value) => {
                  setSearchInput(value ?? "");
                  setSearchText(typeof value === "string" ? value : value?.search_text ?? value?.search_data ?? value?.text ?? "");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Search input" size="small" sx={styles.searchField} />
                )}
              />
            </Box>
            <Button size="small" variant="contained" onClick={handleSearch} sx={styles.addButton}>
              Search
            </Button>
            <FormControl>
              <DateFilter ref={dateRef} onApplycustomdate={handleApplyDateCustom} onApply={handleApplyDate} Onreset={fetchData} />
            </FormControl>
          </Box>

          <Box sx={styles.tableContainer}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress sx={{ color: colors.leaf }} />
                </Box>
              ) : expenseRows.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, px: 2, backgroundColor: "#fafbfc", borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: "#475569", fontWeight: 600 }}>
                    No expenses
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8", mt: 1, textAlign: "center", maxWidth: 320 }}>
                    Add an expense using the &quot;Add New&quot; button or adjust your filters.
                  </Typography>
                </Box>
              ) : (
                <Table sx={{ minWidth: { xs: 650, sm: 750, md: 900 } }} size="small">
                  <TableHead>
                    <TableRow sx={styles.tableHead}>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenseRows.map((row) => (
                      <TableRow key={row.id} sx={styles.tableRow}>
                        <TableCell sx={styles.tableCell}>{formatDate(row.date)}</TableCell>
                        <TableCell sx={styles.tableCell}>{row.description ?? "-"}</TableCell>
                        <TableCell sx={styles.tableCell}>{row.category ?? "-"}</TableCell>
                        <TableCell sx={styles.tableCell}>{row.amount != null ? Number(row.amount) : "-"}</TableCell>
                        <TableCell sx={styles.tableCell}>
                          <Button
                            variant="contained"
                            sx={styles.statusButton(row.status)}
                            disabled={row.status === 1 || statusChangingId === row.id}
                            onClick={() => handleStatusChange(row)}
                          >
                            {statusChangingId === row.id ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : row.status === 1 ? "Paid" : "Pending"}
                          </Button>
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          <IconButton onClick={() => handleEditOpen(row)} sx={{ ...styles.actionButton, color: colors.leaf }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(row.id)} sx={{ ...styles.actionButton, color: "#ef4444" }}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>

          {expenseRows.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={handleLoadMore}
                disabled={!hasMore || loadingMore}
                sx={styles.loadMoreButton}
              >
                {loadingMore ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : expenseRows.length < LIMIT ? "No More Records" : "Load More"}
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      <Dialog open={addModalOpen} maxWidth="xs" fullWidth onClose={handleAddClose} PaperProps={{ sx: styles.dialogPaper }}>
        <DialogTitle sx={styles.dialogTitle}>
          {modalMode === "add" ? "Add Expense" : "Edit Expense"}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            value={fieldDescription}
            error={formSubmitted && !fieldDescription.trim()}
            helperText={formSubmitted && !fieldDescription.trim() ? "Description is required" : ""}
            onChange={(e) => setFieldDescription(e.target.value)}
            sx={styles.searchField}
          />
          <TextField margin="dense" label="Category" fullWidth value={fieldCategory} onChange={(e) => setFieldCategory(e.target.value)} sx={styles.searchField} />
          <TextField
            margin="dense"
            label="Amount"
            fullWidth
            type="number"
            value={fieldAmount}
            error={formSubmitted && !fieldAmount.trim()}
            helperText={formSubmitted && !fieldAmount.trim() ? "Amount is required" : ""}
            onChange={(e) => setFieldAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            sx={styles.searchField}
          />
          <TextField margin="dense" label="Date" fullWidth type="date" value={fieldDate} onChange={(e) => setFieldDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={styles.searchField} />
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button onClick={handleAddClose} sx={{ color: "#64748b", textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleSaveExpense} variant="contained" disabled={saving} sx={styles.addButton}>
            {saving ? <CircularProgress size={24} /> : modalMode === "add" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirmation} onClose={() => !deleting && setOpenDeleteConfirmation(false)} PaperProps={{ sx: { ...styles.dialogPaper, borderRadius: 2 } }}>
        <DialogTitle sx={{ color: colors.leaf }}>Delete Expense</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this expense?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteConfirmation(false)} disabled={deleting} sx={{ color: "#64748b", textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" disabled={deleting} sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" }, textTransform: "none" }}>
            {deleting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <AppToast open={openToast} message={snackbarMessage} severity={snackbarSeverity} onClose={() => setOpenToast(false)} />
    </Box>
  );
}

export default ExpenseListPage;
